import React, { useState, useEffect } from 'react';
import { Calendar, X, FileText, Download, Edit, Trash, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import axiosInstance from "../../../axiosConfig";


export default function Document({ setActiveTab, activeTab, entityUuid, entityId, entityName, entityType, entityCode }) {
  // Remove the hardcoded entityUuid and use the prop instead
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [showDocumentList, setShowDocumentList] = useState(false);
  const [formSections, setFormSections] = useState([{ 
    id: 1, 
    selectedFiles: [],
    branch: '',
    documentType: '',
    issuingAuthority: '',
    issueDate: '',
    expiryDate: '',
    notes: '',
    isBranchRelated: 'no',
    isRenewable: 'no'
  }]);

  // Available branches from API
  const [availableBranches, setAvailableBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  
  // Document guidelines from API
  const [documentGuidelines, setDocumentGuidelines] = useState([]);
  const [guidelinesLoading, setGuidelinesLoading] = useState(false);
  
  // Uploaded documents stored in cache
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  
  // Use props for organization data with fallbacks
  const organizationData = {
    name: entityName || 'Merchant Name',
    registration: entityCode || 'N/A',
    type: entityType || 'N/A',
    id: entityId || '',
    uuid: entityUuid || ''
  };
  
  const [documentsData] = useState([
    {
      id: 1,
      name: 'Business Registration Certificate',
      fileName: 'SLT_BR_2023.pdf',
      type: 'Registration',
      branch: 'Head Office',
      uploadDate: '2023-01-15',
      expiryDate: '2026-01-14',
      status: 'Verified'
    },
    {
      id: 2,
      name: 'Tourism Board License',
      fileName: 'SLTB_LICENSE_2023.pdf',
      type: 'License',
      branch: 'All Branches',
      uploadDate: '2023-06-22',
      expiryDate: '2025-06-21',
      status: 'Verified'
    },
    {
      id: 3,
      name: 'Tax Registration Certificate',
      fileName: 'TAX_REG_2023.pdf',
      type: 'Tax Document',
      branch: 'Head Office',
      uploadDate: '2023-03-10',
      expiryDate: '2026-03-09',
      status: 'Verified'
    }
  ]);
  
  const [filters, setFilters] = useState({
    documentType: 'All Types',
    status: 'All Statuses',
    branch: 'All Branches'
  });

  // Fetch branches and existing documents from API
  const fetchBranches = async () => {
    if (!entityUuid) {
      console.log('No entityUuid available for fetching branches');
      return;
    }
    
    setBranchesLoading(true);
    try {
      const response = await axiosInstance.get(
        `/internal-api/v1/client-web/entity-panel/config/${entityUuid}/view-info`
      );
      
      console.log('View-info API Response:', response.data);
      
        if (response.data && response.data.result) {
        // Load branches
        if (response.data.result.entityBranches) {
          const branches = response.data.result.entityBranches.map(branch => ({
            uuid: branch.uuid,
            name: branch.name,
            id: branch.id,
            entityId: entityId // Use the entityId from props
          }));
          setAvailableBranches(branches);
          console.log('Loaded branches:', branches);
        }
        
        // Load existing documents from API
        if (response.data.result.entityDocuments && response.data.result.entityDocuments.length > 0) {
          const existingDocs = response.data.result.entityDocuments.map(doc => ({
            id: doc.id,
            uuid: doc.uuid,
            fileName: doc.documentFiles?.[0]?.documentName || 'Document',
            documentType: doc.guideline?.name || 'Unknown',
            documentTypeId: doc.guidelineId,
            branch: doc.entityBranchUuid ? 
              response.data.result.entityBranches?.find(b => b.uuid === doc.entityBranchUuid)?.name || 'Branch' 
              : 'Main Entity',
            branchUuid: doc.entityBranchUuid || '',
            issuingAuthority: doc.issuer || '',
            issueDate: doc.issuedAt ? doc.issuedAt.split(' ')[0] : '',
            expiryDate: doc.expiredAt ? doc.expiredAt.split(' ')[0] : '',
            notes: doc.note || '',
            uploadedAt: doc.createdAt || new Date().toISOString(),
            tenantType: doc.entityBranchUuid ? 'ENTITY_BRANCH' : 'ENTITY',
            documentFiles: doc.documentFiles || []
          }));
          setUploadedDocuments(existingDocs);
          console.log('Loaded existing documents:', existingDocs);
        }
      }
    } catch (error) {
      console.error('Error fetching branches and documents:', error);
      alert('Error loading data. Please refresh the page.');
    } finally {
      setBranchesLoading(false);
    }
  };
  
  // Fetch document guidelines from API
  const fetchDocumentGuidelines = async () => {
    setGuidelinesLoading(true);
    try {
      const response = await axiosInstance.get(
        `/internal-api/v1/client-web/entity-panel/registration/${entityId}/entity-doc-guidelines`
      );
      
      console.log('Document Guidelines API Response:', response.data);
      
      
      if (response.data && response.data.result) {
        setDocumentGuidelines(response.data.result);
      }
    } catch (error) {
      console.error('Error fetching document guidelines:', error);
      alert('Error loading document types. Please refresh the page.');
        } finally {
      setGuidelinesLoading(false);
    }
  };

  // Load uploaded documents from localStorage only if API hasn't loaded them
  useEffect(() => {
    if (entityUuid && uploadedDocuments.length === 0) {
      const cacheKey = `uploadedDocuments_${entityUuid}`;
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        try {
          setUploadedDocuments(JSON.parse(cached));
        } catch (error) {
          console.error('Error parsing cached documents:', error);
        }
      }
    }
  }, [entityUuid, uploadedDocuments.length]);
  
  // Fetch branches and guidelines on component mount
  useEffect(() => {
    fetchBranches();
    fetchDocumentGuidelines();
    // Log the entity UUID being used for debugging
    console.log('Using Entity UUID:', entityUuid);
  }, [entityUuid]);

  const toggleUploadForm = () => {
    setShowUploadForm(!showUploadForm);
    if (showUploadForm) {
      setFormSections([{ 
        id: 1, 
        selectedFiles: [],
        branch: '',
        documentType: '',
        issuingAuthority: '',
        issueDate: '',
        expiryDate: '',
        notes: '',
        isBranchRelated: 'no',
        isRenewable: 'no'
      }]);
    }
    // Close document list if open
    if (showDocumentList) {
      setShowDocumentList(false);
    }
  };
  
  const toggleDocumentList = () => {
    setShowDocumentList(!showDocumentList);
    // Close upload form if open
    if (showUploadForm) {
      setShowUploadForm(false);
    }
  };

  const handleFileSelect = (formId, event) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      const section = formSections.find(s => s.id === formId);
      if (section && section.documentType) {
        const guideline = documentGuidelines.find(g => g.id === parseInt(section.documentType));
        if (guideline && files.length > guideline.maxNo) {
          alert(`You can only select up to ${guideline.maxNo} file(s) for "${guideline.name}".`);
          event.target.value = ''; // Clear the input
          return;
        }
      }
      updateFormFiles(formId, files);
    }
  };

  const handleDrop = (formId, event) => {
    event.preventDefault();
    event.stopPropagation();
    
    const files = Array.from(event.dataTransfer.files || []);
    if (files.length > 0) {
      const section = formSections.find(s => s.id === formId);
      if (section && section.documentType) {
        const guideline = documentGuidelines.find(g => g.id === parseInt(section.documentType));
        if (guideline && files.length > guideline.maxNo) {
          alert(`You can only select up to ${guideline.maxNo} file(s) for "${guideline.name}".`);
          return;
        }
      }
      updateFormFiles(formId, files);
    }
  };

  const updateFormFiles = (formId, files) => {
    setFormSections(prevSections => 
      prevSections.map(section => 
        section.id === formId ? { ...section, selectedFiles: files } : section
      )
    );
  };
  
  const removeFile = (formId, fileIndex) => {
    setFormSections(prevSections => 
      prevSections.map(section => {
        if (section.id === formId) {
          const newFiles = section.selectedFiles.filter((_, index) => index !== fileIndex);
          return { ...section, selectedFiles: newFiles };
        }
        return section;
      })
    );
  };

  const handleFormFieldChange = (formId, field, value) => {
    setFormSections(prevSections => 
      prevSections.map(section => {
        if (section.id === formId) {
          const updated = { ...section, [field]: value };
          // Clear branch if switching to 'no'
          if (field === 'isBranchRelated' && value === 'no') {
            updated.branch = '';
          }
          // Clear expiry date if switching to 'no'
          if (field === 'isRenewable' && value === 'no') {
            updated.expiryDate = '';
          }
          return updated;
        }
        return section;
      })
    );
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const addNewForm = () => {
    const newId = formSections.length > 0 
      ? Math.max(...formSections.map(section => section.id)) + 1 
      : 1;
    
    setFormSections([...formSections, { 
      id: newId, 
      selectedFiles: [],
      branch: '',
      documentType: '',
      issuingAuthority: '',
      issueDate: '',
      expiryDate: '',
      notes: '',
      isBranchRelated: 'no',
      isRenewable: 'no'
    }]);
  };

  const removeForm = (formId) => {
    if (formSections.length > 1) {
      setFormSections(formSections.filter(section => section.id !== formId));
    }
  };
  
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const formatDateTime = (dateStr) => {
    return dateStr ? `${dateStr} 10:00:00` : '';
  };
  
  const saveDocumentToCache = (documentInfo) => {
    const updatedDocs = [...uploadedDocuments, documentInfo];
    setUploadedDocuments(updatedDocs);
    
    // Save to localStorage
    if (entityUuid) {
      const cacheKey = `uploadedDocuments_${entityUuid}`;
      localStorage.setItem(cacheKey, JSON.stringify(updatedDocs));
    }
  };
  
  const handleAddDocument = async () => {
    // Same validation and upload logic as handleSaveClick, but doesn't navigate
    if (!entityUuid) {
      alert('Entity UUID is required. Please complete the basic information first.');
      return;
    }

    let hasError = false;
    let message = '';

    // Validation
    formSections.forEach((section, index) => {
      if (!section.selectedFiles || section.selectedFiles.length === 0) {
        hasError = true;
        message = `Please select at least one file for Document ${index + 1}`;
      } else if (!section.documentType) {
        hasError = true;
        message = `Please select document type for Document ${index + 1}`;
      } else if (!section.issuingAuthority) {
        hasError = true;
        message = `Please enter issuing authority for Document ${index + 1}`;
      } else if (!section.issueDate) {
        hasError = true;
        message = `Please set issue date for Document ${index + 1}`;
      }
    });

    if (hasError) {
      alert(message);
      return;
    }

    if (formSections.length === 0 || !formSections.some(section => section.selectedFiles && section.selectedFiles.length > 0)) {
      alert('Please upload at least one document');
      return;
    }

    const firstSection = formSections[0];
    
    // Validate guideline requirements
    const selectedGuideline = documentGuidelines.find(g => g.id === parseInt(firstSection.documentType));
    if (selectedGuideline) {
      const fileCount = formSections.reduce((count, s) => count + (s.selectedFiles?.length || 0), 0);
      if (fileCount < selectedGuideline.minNo) {
        alert(`The selected document type "${selectedGuideline.name}" requires at least ${selectedGuideline.minNo} file(s). You have only selected ${fileCount} file(s).`);
        return;
      }
      if (fileCount > selectedGuideline.maxNo) {
        alert(`The selected document type "${selectedGuideline.name}" allows maximum ${selectedGuideline.maxNo} file(s). You have selected ${fileCount} file(s).`);
        return;
      }
    }
    const selectedBranchUuid = firstSection.branch || '';
    const tenantType = selectedBranchUuid ? 'ENTITY_BRANCH' : 'ENTITY';

    const formdata = new FormData();
    
    // Append all selected files
    if (firstSection.selectedFiles && firstSection.selectedFiles.length > 0) {
      firstSection.selectedFiles.forEach(file => {
        formdata.append('files[]', file);
      });
    }
    
    formdata.append('entityUuid', entityUuid);
    formdata.append('entityBranchUuid', selectedBranchUuid);
    formdata.append('tenantType', tenantType);
    formdata.append('guidelineId', firstSection.documentType);
    formdata.append('issuer', firstSection.issuingAuthority);
    formdata.append('issuedAt', firstSection.issueDate ? formatDateTime(firstSection.issueDate) : '');
    formdata.append('expireAt', firstSection.isRenewable === 'yes' && firstSection.expiryDate ? formatDateTime(firstSection.expiryDate) : '');
    formdata.append('isRenewableRequired', firstSection.isRenewable === 'yes' ? '1' : '0');
    formdata.append('note', firstSection.notes || '');

    console.log('FormData contents (Add Document):');
    for (let [key, value] of formdata.entries()) {
      console.log(`${key}:`, value instanceof File ? `File: ${value.name} (${value.size} bytes)` : value);
    }

    try {
      const response = await axiosInstance.post(
        '/internal-api/v1/client-web/entity-panel/registration/upload-entity-doc',
        formdata,
      );

      if (response.data && response.data.result) {
        // Get document type name
        const selectedGuideline = documentGuidelines.find(g => g.id === parseInt(firstSection.documentType));
        const selectedBranch = availableBranches.find(b => b.uuid === selectedBranchUuid);
        
        // Save document info to cache
        const documentInfo = {
          id: Date.now(),
          fileName: firstSection.selectedFiles.map(f => f.name).join(', '),
          documentType: selectedGuideline?.name || 'Unknown',
          documentTypeId: firstSection.documentType,
          branch: selectedBranch?.name || 'Main Entity',
          branchUuid: selectedBranchUuid,
          issuingAuthority: firstSection.issuingAuthority,
          issueDate: firstSection.issueDate,
          expiryDate: firstSection.expiryDate,
          notes: firstSection.notes,
          uploadedAt: new Date().toISOString(),
          tenantType: tenantType
        };
        
        saveDocumentToCache(documentInfo);
        
        alert('Document added successfully!');
        
        // Close upload form and show the uploaded documents table
        setShowUploadForm(false);
        
        // Reset form
        setFormSections([{ 
          id: 1, 
          selectedFiles: [],
          branch: '',
          documentType: '',
          issuingAuthority: '',
          issueDate: '',
          expiryDate: '',
          notes: '',
          isBranchRelated: 'no',
          isRenewable: 'no'
        }]);
        
        // Scroll to uploaded documents section
        setTimeout(() => {
          const element = document.getElementById('uploaded-documents-section');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      } else {
        alert('Document upload completed, but please verify the result.');
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      let errorMessage = 'Error uploading document. ';
      if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.message) {
          errorMessage += errorData.message;
        } else if (errorData.error) {
          errorMessage += errorData.error;
        } else if (typeof errorData === 'string') {
          errorMessage += errorData;
        } else {
          errorMessage += JSON.stringify(errorData);
        }
      } else {
        errorMessage += error.message || 'Please try again.';
      }
      alert(errorMessage);
    }
  };

  const handleSaveClick = async () => {
    // Check if entityUuid is provided
    if (!entityUuid) {
      alert('Entity UUID is required. Please complete the basic information first.');
      return;
    }

    let hasError = false;
    let message = '';

    // Validation
    formSections.forEach((section, index) => {
      if (!section.selectedFiles || section.selectedFiles.length === 0) {
        hasError = true;
        message = `Please select at least one file for Document ${index + 1}`;
      } else if (!section.documentType) {
        hasError = true;
        message = `Please select document type for Document ${index + 1}`;
      } else if (!section.issuingAuthority) {
        hasError = true;
        message = `Please enter issuing authority for Document ${index + 1}`;
      } else if (!section.issueDate) {
        hasError = true;
        message = `Please set issue date for Document ${index + 1}`;
      }
    });

    if (hasError) {
      alert(message);
      return;
    }

    // Check if at least one document is uploaded
    if (formSections.length === 0 || !formSections.some(section => section.selectedFiles && section.selectedFiles.length > 0)) {
      alert('Please upload at least one document');
      return;
    }

    // Get the entityBranchUuid from the selected branch (optional)
    const firstSection = formSections[0];
    
    // Validate guideline requirements
    const selectedGuideline = documentGuidelines.find(g => g.id === parseInt(firstSection.documentType));
    if (selectedGuideline) {
      const fileCount = formSections.reduce((count, s) => count + (s.selectedFiles?.length || 0), 0);
      if (fileCount < selectedGuideline.minNo) {
        alert(`The selected document type "${selectedGuideline.name}" requires at least ${selectedGuideline.minNo} file(s). You have only selected ${fileCount} file(s).`);
        return;
      }
      if (fileCount > selectedGuideline.maxNo) {
        alert(`The selected document type "${selectedGuideline.name}" allows maximum ${selectedGuideline.maxNo} file(s). You have selected ${fileCount} file(s).`);
        return;
      }
    }
    const selectedBranchUuid = firstSection.branch || '';
    
    // Determine tenantType based on whether branch is selected
    const tenantType = selectedBranchUuid ? 'ENTITY_BRANCH' : 'ENTITY';

    const formdata = new FormData();
    
    // Add all selected files (matching Postman order)
    if (firstSection.selectedFiles && firstSection.selectedFiles.length > 0) {
      firstSection.selectedFiles.forEach(file => {
        formdata.append('files[]', file);
      });
    }
    
    // Add entity information - use the prop entityUuid
    formdata.append('entityUuid', entityUuid);
    formdata.append('entityBranchUuid', selectedBranchUuid);
    formdata.append('tenantType', tenantType);
    formdata.append('guidelineId', firstSection.documentType);
    formdata.append('issuer', firstSection.issuingAuthority);
    
    // Add dates and notes from first form section (as per API structure)
    // Always add these fields (server expects them even if empty)
    formdata.append('issuedAt', firstSection.issueDate ? formatDateTime(firstSection.issueDate) : '');
    formdata.append('expireAt', firstSection.isRenewable === 'yes' && firstSection.expiryDate ? formatDateTime(firstSection.expiryDate) : '');
    formdata.append('isRenewableRequired', firstSection.isRenewable === 'yes' ? '1' : '0');
    formdata.append('note', firstSection.notes || '');

    console.log('FormData contents:');
    for (let [key, value] of formdata.entries()) {
      console.log(`${key}:`, value instanceof File ? `File: ${value.name} (${value.size} bytes)` : value);
    }

    try {
      const response = await axiosInstance.post(
        '/internal-api/v1/client-web/entity-panel/registration/upload-entity-doc',
        formdata,
      );

      console.log("Full Response:", response);
      console.log("Response Status:", response.status);
      console.log("Response Data:", response.data);

      if (response.data && response.data.result) {
        console.log("Form submitted successfully:", response.data.result);
        
        // Show success message and then move to next tab
        alert('Document uploaded successfully!');
        
        // Reset form after successful upload
        setShowUploadForm(false);
        setFormSections([{ 
          id: 1, 
          selectedFiles: [],
          branch: '',
          documentType: '',
          issuingAuthority: '',
          issueDate: '',
          expiryDate: '',
          notes: '',
          isBranchRelated: 'no',
          isRenewable: 'no'
        }]);
        
        // Move to the next tab (Bank Details)
        if (setActiveTab) {
          setActiveTab("contact");
        }
      } else {
        console.warn("Success response but no result data:", response.data);
        alert('Document upload completed, but please verify the result.');
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      console.error("Error message:", error.message);
      
      // Show detailed error message
      let errorMessage = 'Error uploading document. ';
      if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.message) {
          errorMessage += errorData.message;
        } else if (errorData.error) {
          errorMessage += errorData.error;
        } else if (typeof errorData === 'string') {
          errorMessage += errorData;
        } else {
          errorMessage += JSON.stringify(errorData);
        }
      } else {
        errorMessage += error.message || 'Please try again.';
      }
      
      alert(errorMessage);
    }
  };

  return (
    
    <div className="merchant-container">
      {/* Show info message if no entity data is available */}
      {!entityId && !entityUuid ? (
        <div className="p-6">
          <div className="p-6 text-center rounded bg-blue-50">
            <h3 className="mb-3 text-base font-medium text-gray-700">Document Management</h3>
            <p className="mb-2 text-sm text-gray-600">Please complete the Basic Info step first to enable document management.</p>
            <p className="mt-3 text-xs text-gray-600">After saving the merchant, you will be able to upload documents such as:</p>
            <p className="mt-2 text-xs text-gray-600">
              • Business Registration Certificate • Tourism Board License • Tax Registration Certificate
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Organization Header */}
          <div className="merchant-header">
            <h3 className="merchant-title">{organizationData.name}</h3>
            <div className="merchant-subtitle">
              Registration: {organizationData.registration}
            </div>
            <div className="merchant-subtitle">
              Type: {organizationData.type}
            </div>
            {/* {merchantInfo.uuid && (
              <div className="text-xs text-gray-500 merchant-subtitle">
                UUID: {merchantInfo.uuid} | Entity ID: {merchantInfo.id}
              </div>
            )} */}
          </div>

          <div className="pt-4 mb-5 border-t">
            <h4 className="mb-2 text-base font-medium">Document Management Information</h4>
            <ul className="pl-6 text-xs text-gray-600 list-disc">
              <li>All documents are securely stored and only accessible to authorized personnel</li>
              <li>Documents expiring within 30 days will be flagged for renewal in the dashboard</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 mb-6">
            <button 
              className="flex items-center px-4 py-2 text-xs text-blue-500 border border-blue-500 rounded-md"
              onClick={toggleDocumentList}
            >
              <span className="mr-1">📄</span> Document List
            </button>
            <button 
              className="flex items-center px-4 py-2 text-xs text-white bg-blue-500 rounded-md"
              onClick={toggleUploadForm}
            >
              <span className="mr-1">+</span> Upload New Document
            </button>
          </div>
          
          {/* Uploaded Documents Section */}
          {!showUploadForm && !showDocumentList && uploadedDocuments.length > 0 && (
            <div id="uploaded-documents-section" className="mb-6">
              <h3 className="mb-4 text-base font-medium">Uploaded Documents ({uploadedDocuments.length})</h3>
              <div className="overflow-hidden border border-gray-200 rounded-md">
                <table className="w-full text-xs">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left">Document Type</th>
                      <th className="px-4 py-2 text-left">Branch</th>
                      <th className="px-4 py-2 text-left">Issuing Authority</th>
                      <th className="px-4 py-2 text-left">Issue Date</th>
                      <th className="px-4 py-2 text-left">Expiry Date</th>
                      <th className="px-4 py-2 text-left">Uploaded At</th>
                      <th className="px-4 py-2 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadedDocuments.map((doc) => (
                      <tr key={doc.id} className="border-t border-gray-200">
                        <td className="px-4 py-3">{doc.documentType}</td>
                        <td className="px-4 py-3">{doc.branch}</td>
                        <td className="px-4 py-3">{doc.issuingAuthority}</td>
                        <td className="px-4 py-3">{doc.issueDate || '-'}</td>
                        <td className="px-4 py-3">{doc.expiryDate || '-'}</td>
                        <td className="px-4 py-3">
                          {new Date(doc.uploadedAt).toLocaleDateString()}
                        </td>
                        <td className="table-cell">
                        {/* <button className="p-1 action-btn action-btn-blue"><Eye size={16} /></button>
                        <button className="p-1 action-btn action-btn-yellow"><Edit size={16} /></button> */}
                        <button className="p-1 action-btn action-btn-red"><Trash2 size={16} /></button>
                      </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button 
                  className="px-6 py-2 text-xs text-white bg-blue-500 rounded-md"
                  onClick={() => setActiveTab && setActiveTab('contact')}
                >
                  Next: Bank Details
                </button>
              </div>
            </div>
          )}

      {/* Document List Dialog */}
      {showDocumentList && (
        <div className="mb-6 overflow-hidden border border-gray-200 rounded-md">
          {/* Filters */}
          <div className="grid grid-cols-4 gap-4 p-4 bg-gray-50">
            <div>
              <label className="block mb-1 text-xs font-medium">Document Type</label>
              <div className="relative">
                <select 
                  className="w-full p-2 text-xs bg-white border border-gray-300 rounded-md"
                  value={filters.documentType}
                  onChange={(e) => handleFilterChange('documentType', e.target.value)}
                >
                  <option>All Types</option>
                  <option>Registration</option>
                  <option>License</option>
                  <option>Tax Document</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block mb-1 text-xs font-medium">Status</label>
              <div className="relative">
                <select 
                  className="w-full p-2 text-xs bg-white border border-gray-300 rounded-md"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option>All Statuses</option>
                  <option>Verified</option>
                  <option>Pending</option>
                  <option>Expired</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block mb-1 text-xs font-medium">Branch</label>
              <div className="relative">
                <select 
                  className="w-full p-2 text-xs bg-white border border-gray-300 rounded-md"
                  value={filters.branch}
                  onChange={(e) => handleFilterChange('branch', e.target.value)}
                >
                  <option>All Branches</option>
                  <option>Head Office</option>
                  <option>Regional Office</option>
                </select>
              </div>
            </div>
            
            <div className="flex items-end">
              <button className="w-full px-4 py-2 text-xs text-white bg-blue-500 rounded-md">
                Filter
              </button>
            </div>
          </div>
          
          {/* Document Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-left">Document Name</th>
                  <th className="px-4 py-2 text-left">Document Type</th>
                  <th className="px-4 py-2 text-left">Branch</th>
                  <th className="px-4 py-2 text-left">Upload Date</th>
                  <th className="px-4 py-2 text-left">Expiry Date</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {documentsData.map(doc => (
                  <tr key={doc.id} className="border-t border-gray-200">
                    <td className="px-4 py-3">
                      <div>
                        <div className="font-medium">{doc.name}</div>
                        <div className="text-gray-500">{doc.fileName}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3">{doc.type}</td>
                    <td className="px-4 py-3">{doc.branch}</td>
                    <td className="px-4 py-3">{doc.uploadDate}</td>
                    <td className="px-4 py-3">{doc.expiryDate}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 text-xs text-green-800 bg-green-100 rounded-full">
                        {doc.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <button className="text-gray-500 hover:text-gray-700">
                          <FileText className="w-4 h-4" />
                        </button>
                        <button className="text-blue-500 hover:text-blue-700">
                          <Download className="w-4 h-4" />
                        </button>
                        <button className="text-orange-500 hover:text-orange-700">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button className="text-gray-500 hover:text-gray-700">
                          <Trash className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <div className="flex items-center justify-between p-3 bg-gray-50">
            <div className="text-xs text-gray-600">
              Showing 1-3 of 12 documents
            </div>
            <div className="flex items-center space-x-1">
              <button className="px-2 py-1 text-gray-600 bg-white border border-gray-300 rounded-md">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button className="px-3 py-1 text-white bg-blue-500 border border-blue-500 rounded-md">
                1
              </button>
              <button className="px-3 py-1 text-gray-600 bg-white border border-gray-300 rounded-md">
                2
              </button>
              <button className="px-3 py-1 text-gray-600 bg-white border border-gray-300 rounded-md">
                3
              </button>
              <button className="px-2 py-1 text-gray-600 bg-white border border-gray-300 rounded-md">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Form (conditionally rendered) */}
      {showUploadForm && entityUuid && (
        <div className="mb-6">
          <h3 className="mb-4 text-base font-medium">Upload New Document</h3>
          
          {/* Multiple Form Sections */}
          {formSections.map((formSection, index) => (
            <div 
              key={formSection.id} 
              className={`p-4 rounded-md ${index > 0 ? 'mt-6 border-t pt-8' : ''}`}
            >
              
              
              {/* Form Fields */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                {/* Left Column */}
                <div>
                  <div className="mb-4">
                    <label className="block mb-2 text-xs font-medium">
                      Branch Related doc?
                    </label>
                    <div className="flex gap-4 mb-2">
                      <label className="flex items-center text-xs">
                        <input
                          type="radio"
                          name={`branchRelated_${formSection.id}`}
                          value="yes"
                          checked={formSection.isBranchRelated === 'yes'}
                          onChange={(e) => handleFormFieldChange(formSection.id, 'isBranchRelated', e.target.value)}
                          className="mr-1"
                        />
                        YES
                      </label>
                      <label className="flex items-center text-xs">
                        <input
                          type="radio"
                          name={`branchRelated_${formSection.id}`}
                          value="no"
                          checked={formSection.isBranchRelated === 'no'}
                          onChange={(e) => handleFormFieldChange(formSection.id, 'isBranchRelated', e.target.value)}
                          className="mr-1"
                        />
                        NO
                      </label>
                    </div>
                    {formSection.isBranchRelated === 'yes' && (
                      <div className="relative">
                        <select 
                          className="w-full p-2 text-xs bg-white border border-gray-300 rounded-md"
                          value={formSection.branch}
                          onChange={(e) => handleFormFieldChange(formSection.id, 'branch', e.target.value)}
                          disabled={branchesLoading}
                        >
                          <option value="">
                            {branchesLoading ? 'Loading branches...' : 'Select branch'}
                          </option>
                          {availableBranches
                            .filter(branch => branch.entityId === entityId)
                            .map(branch => (
                              <option key={branch.uuid} value={branch.uuid}>
                                {branch.name}
                              </option>
                            ))}
                        </select>
                      </div>
                    )}
                  </div>
                  
                  <div className="mb-4">
                    <label className="block mb-1 text-xs">
                      Issuing Authority <span className="text-red-500">*</span>
                    </label>
                    <input 
                      type="text" 
                      placeholder="Enter issuing authority"
                      className="w-full p-2 text-xs border border-gray-300 rounded-md"
                      value={formSection.issuingAuthority}
                      onChange={(e) => handleFormFieldChange(formSection.id, 'issuingAuthority', e.target.value)}
                      required
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label className="block mb-2 text-xs font-medium">
                      A Renewable Doc?
                    </label>
                    <div className="flex gap-4 mb-2">
                      <label className="flex items-center text-xs">
                        <input
                          type="radio"
                          name={`renewable_${formSection.id}`}
                          value="yes"
                          checked={formSection.isRenewable === 'yes'}
                          onChange={(e) => handleFormFieldChange(formSection.id, 'isRenewable', e.target.value)}
                          className="mr-1"
                        />
                        YES
                      </label>
                      <label className="flex items-center text-xs">
                        <input
                          type="radio"
                          name={`renewable_${formSection.id}`}
                          value="no"
                          checked={formSection.isRenewable === 'no'}
                          onChange={(e) => handleFormFieldChange(formSection.id, 'isRenewable', e.target.value)}
                          className="mr-1"
                        />
                        NO
                      </label>
                    </div>
                    {formSection.isRenewable === 'yes' && (
                      <div className="relative">
                        <label className="block mb-1 text-xs">
                          Expiry Date <span className="text-red-500">*</span>
                        </label>
                        <input 
                          type="date" 
                          className="w-full p-2 text-xs border border-gray-300 rounded-md"
                          value={formSection.expiryDate}
                          onChange={(e) => handleFormFieldChange(formSection.id, 'expiryDate', e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Right Column */}
                <div>
                  <div className="mb-4">
                    <label className="block mb-1 text-xs">
                      Document Type <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select 
                        className="w-full p-2 text-xs bg-white border border-gray-300 rounded-md"
                        value={formSection.documentType}
                        onChange={(e) => handleFormFieldChange(formSection.id, 'documentType', e.target.value)}
                        disabled={guidelinesLoading}
                        required
                      >
                        <option value="">
                          {guidelinesLoading ? 'Loading document types...' : 'Select document type'}
                        </option>
                        {documentGuidelines.map(guideline => (
                          <option key={guideline.id} value={guideline.id}>
                            {guideline.name} {guideline.isRequired ? '*' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block mb-1 text-xs">
                      Issue Date <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input 
                        type="date" 
                        className="w-full p-2 text-xs border border-gray-300 rounded-md"
                        value={formSection.issueDate}
                        onChange={(e) => handleFormFieldChange(formSection.id, 'issueDate', e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block mb-1 text-xs">
                      Notes
                    </label>
                    <textarea 
                      placeholder="Enter any additional notes"
                      className="w-full p-2 text-xs border border-gray-300 rounded-md" 
                      rows={1}
                      value={formSection.notes}
                      onChange={(e) => handleFormFieldChange(formSection.id, 'notes', e.target.value)}
                    />
                  </div>
                </div>
              </div>
              
              {index > 0 && (
                <div className="flex justify-between mb-4">
                  <h4 className="text-base font-medium">Document {index + 1}</h4>
                  <button 
                    className="flex items-center text-xs text-red-500" 
                    onClick={() => removeForm(formSection.id)}
                  >
                    <X className="w-4 h-4 mr-1" /> Remove
                  </button>
                </div>
              )}
              
              {/* File Upload Area */}
              <div 
                className="p-8 mb-4 text-center border-2 border-gray-300 border-dashed rounded-md"
                onDrop={(e) => handleDrop(formSection.id, e)}
                onDragOver={handleDragOver}
              >
                {formSection.selectedFiles && formSection.selectedFiles.length > 0 ? (
                  <div className="space-y-2">
                    {formSection.selectedFiles.map((file, fileIndex) => (
                      <div key={fileIndex} className="flex items-center justify-between px-4 py-2 rounded bg-green-50">
                        <p className="text-xs text-green-600">{file.name} ({(file.size / 1024).toFixed(2)} KB)</p>
                        <button 
                          className="px-2 py-1 text-xs text-red-600 rounded hover:bg-red-100"
                          onClick={() => removeFile(formSection.id, fileIndex)}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <label className="inline-block px-4 py-2 mt-2 text-xs text-white bg-blue-500 rounded-md cursor-pointer">
                      Add More Files
                      <input 
                        type="file" 
                        multiple
                        accept={formSection.documentType ? documentGuidelines.find(g => g.id === parseInt(formSection.documentType))?.supportedFileTypes : 'image/*'}
                        className="hidden" 
                        onChange={(e) => handleFileSelect(formSection.id, e)}
                      />
                    </label>
                  </div>
                ) : (
                  <>
                    {formSection.documentType && (() => {
                      const guideline = documentGuidelines.find(g => g.id === parseInt(formSection.documentType));
                      return guideline ? (
                        <p className="mb-2 text-xs font-medium text-blue-600">
                          Accepted: {guideline.fileTypeName} | Max {guideline.maxNo} file(s) | Size: {guideline.minSizeKb}-{guideline.maxSizeInKb} KB
                        </p>
                      ) : null;
                    })()}
                    <p className="mb-2 text-xs text-gray-500">Drag and drop files here</p>
                    <p className="mb-4 text-xs text-gray-400">or</p>
                    <label className="px-4 py-2 text-xs text-white bg-blue-500 rounded-md cursor-pointer">
                      Browse Files
                      <input 
                        type="file" 
                        multiple
                        accept={formSection.documentType ? documentGuidelines.find(g => g.id === parseInt(formSection.documentType))?.supportedFileTypes : 'image/*'}
                        className="hidden" 
                        onChange={(e) => handleFileSelect(formSection.id, e)}
                      />
                    </label>
                  </>
                )}
              </div>
              
            </div>
          ))}
          
          <div className="mb-4 text-xs text-gray-500">
            <span className="text-red-500">*</span> Required fields
          </div>
          
          <div className="flex justify-end gap-2 mb-4">
            <button 
              className="px-6 py-2 text-xs text-white bg-green-500 rounded-md"
              onClick={handleAddDocument}
            >
              Add Document
            </button>
            <button 
              className="px-6 py-2 text-xs text-white bg-green-500 rounded-md"
              onClick={addNewForm}
            >
              Add New
            </button>
            <button 
              className="px-6 py-2 text-xs text-white bg-blue-500 rounded-md"
              onClick={toggleUploadForm}
            >
              Close
            </button>
          </div>
          
          
          
          {/* <div className="flex justify-end gap-2 mt-6">
            <button 
              className="px-6 py-2 text-xs border border-gray-300 rounded-md"
              onClick={toggleUploadForm}
            >
              Cancel
            </button>
            <button 
              className="px-6 py-2 text-xs text-white bg-blue-500 rounded-md"
              onClick={handleSaveClick}
            >
              Next
            </button>
          </div> */}
        </div>
      )}
        </>
      )}
    </div>
  );
  
}
